/**
 * Shared-prop instancing + distance LOD (P1-T07).
 *
 * Instance lists (cell *_inst.json) reference shared meshes from the
 * manifest's `shared` map. Every prop kind gets up to three thin-instance
 * pools — LOD0 (near), LOD1 (mid) and impostor cards (trees beyond the mid
 * ring, SPEC-02 §2 TRE: 3000 / 600 / impostor) — and instances re-bucket as
 * the camera moves. Counts are in the low thousands, so a throttled full
 * rebuild beats incremental bookkeeping.
 *
 * Instance matrices arrive from the exporter in client space
 * (x east, y up, z north, row-major) and always have the rot_y / trans
 * structure [c,0,-s,0 | 0,1,0,0 | s,0,c,0 | x,y,z,1]. That lets us apply
 * per-instance scale jitter (rule 6) and the 90-degree impostor cross-card
 * as plain arithmetic on (c, s).
 */
import { AssetContainer, Mesh, Scene, SceneLoader, TransformNode, Vector3, type Nullable } from 'babylonjs';
import type { Tier } from '@kota-nova/shared';
import { configureAssetLoader } from './loaderSetup';
import type { InstanceList } from './streamingClient';

const NEAR_SQ = 45 * 45; // LOD0 radius
const MID_SQ = 150 * 150; // LOD1 up to here, impostor beyond
const LOW_NEAR_SQ = 25 * 25; // tier=low tightens both
const LOW_MID_SQ = 90 * 90;
const REBUILD_SQ = 4 * 4; // re-bucket only after the camera moved this far
const UPDATE_INTERVAL_MS = 400;
const CAP = 4096;

type Slot = 'near' | 'mid' | 'far';

interface Pool {
  /** every primitive mesh of the prop GLB (trunk+canopy, pole+head, ...) */
  meshes: Mesh[];
  buf: Float32Array;
  committed: number;
}

interface TypePools {
  near: Nullable<Pool>;
  mid: Nullable<Pool>;
  far: Nullable<Pool>;
}

export interface PropFieldOpts {
  scene: Scene;
  tier: Tier;
  /** run per-shared-container setup (plugins, sign registration) */
  onContainer?: (container: AssetContainer) => void;
}

function hash2(x: number, z: number): number {
  const n = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

export class PropField {
  private readonly root: TransformNode;
  private readonly pools = new Map<string, TypePools>();
  private readonly entries = new Map<string, InstanceList['instances']>();
  private readonly containers: AssetContainer[] = [];
  private lastCam = new Vector3(1e9, 0, 0);
  private dirty = true;
  private lastUpdateMs = -1e9;

  constructor(private readonly opts: PropFieldOpts) {
    this.root = new TransformNode('props', opts.scene);
  }

  /** Load every shared mesh once; call before streaming starts. */
  async loadShared(shared: Record<string, { url: string }>, assetBase: string): Promise<number> {
    configureAssetLoader();
    const { scene } = this.opts;
    let loaded = 0;
    for (const [key, file] of Object.entries(shared)) {
      const lod1 = /^(.+)_lod1$/.exec(key);
      const imp = /^(.+)_imp$/.exec(key);
      const base = lod1?.[1] ?? imp?.[1] ?? key;
      const slot: Slot = lod1 ? 'mid' : imp ? 'far' : 'near';
      try {
        const container = await SceneLoader.LoadAssetContainerAsync(
          `${assetBase}/${file.url}`,
          undefined,
          scene,
        );
        this.containers.push(container);
        container.addAllToScene();
        this.opts.onContainer?.(container);
        const meshes = container.meshes.filter((x) => x.getTotalIndices() > 0);
        if (meshes.length === 0) continue;
        for (const mesh of meshes) {
          mesh.parent = this.root;
          mesh.isPickable = false;
          mesh.alwaysSelectAsActiveMesh = true;
        }
        this.register(base, slot, meshes as Mesh[]);
        loaded++;
      } catch (err) {
        console.warn(`[props] shared ${key} failed:`, (err as Error).message);
      }
    }
    this.dirty = true;
    return loaded;
  }

  private register(base: string, slot: Slot, meshes: Mesh[]): void {
    let pools = this.pools.get(base);
    if (!pools) {
      pools = { near: null, mid: null, far: null };
      this.pools.set(base, pools);
    }
    const buf = new Float32Array(CAP * 16);
    park(buf, 0);
    for (const mesh of meshes) {
      // every primitive of the prop (trunk+canopy, pole+head) shares ONE
      // matrix buffer so they always move together
      mesh.thinInstanceSetBuffer('matrix', buf, 16, false);
      // a thin-instance count of 0 recompiles the material without INSTANCES
      // while the buffer stays bound — always keep one parked matrix visible
      mesh.thinInstanceCount = 1;
      mesh.thinInstanceBufferUpdated('matrix');
    }
    pools[slot] = { meshes, buf, committed: 1 };
  }

  addCell(cellId: string, list: InstanceList): void {
    this.entries.set(cellId, list.instances);
    this.dirty = true;
  }

  /**
   * P1-T10: prop meshes that may cast. SPEC-01 §6 allows only near TREES among
   * props (benches, bins, lamps, drains, signals and glow pools are not
   * casters), so this returns tree pools — keeping the always-pass thin-instance
   * share of the caster list low enough for R-03 (near cascade ≤ 25%).
   */
  casterMeshes(): Mesh[] {
    const out: Mesh[] = [];
    for (const [base, pools] of this.pools) {
      if (!base.startsWith('tree_') || !pools.near) continue;
      out.push(...pools.near.meshes);
    }
    return out;
  }

  removeCell(cellId: string): void {
    if (this.entries.delete(cellId)) this.dirty = true;
  }

  /** Per-frame hook; re-buckets at most every UPDATE_INTERVAL_MS. */
  update(camX: number, camZ: number, nowMs: number): void {
    if (nowMs - this.lastUpdateMs < UPDATE_INTERVAL_MS) return;
    const dx = camX - this.lastCam.x;
    const dz = camZ - this.lastCam.z;
    if (!this.dirty && dx * dx + dz * dz < REBUILD_SQ) return;
    this.lastUpdateMs = nowMs;
    this.lastCam.set(camX, 0, camZ);
    this.dirty = false;
    this.rebuild();
  }

  /** 0 = LOD0 pool, 1 = LOD1, 2 = impostor. */
  slotFor(d2: number): Slot {
    const low = this.opts.tier === 'low';
    if (d2 < (low ? LOW_NEAR_SQ : NEAR_SQ)) return 'near';
    if (d2 < (low ? LOW_MID_SQ : MID_SQ)) return 'mid';
    return 'far';
  }

  private rebuild(): void {
    const written = new Map<string, number>(); // `${base}:${slot}` -> next index
    const take = (base: string, slot: Slot): number => {
      const key = `${base}:${slot}`;
      const idx = written.get(key) ?? 0;
      written.set(key, idx + 1);
      return idx;
    };
    for (const list of this.entries.values()) {
      for (const inst of list) {
        const m = inst.m;
        const x = m[12] ?? 0;
        const z = m[14] ?? 0;
        const dx = x - this.lastCam.x;
        const dz = z - this.lastCam.z;
        const d2 = dx * dx + dz * dz;
        const pools = this.pools.get(inst.type);
        if (pools) {
          const want = this.slotFor(d2);
          const slot: Slot = pools[want] ? want : pools.near ? 'near' : pools.mid ? 'mid' : 'far';
          const pool = pools[slot];
          if (pool) {
            const jitter = inst.type.startsWith('tree') || inst.type === 'planter';
            let idx = take(inst.type, slot);
            if (idx < CAP) writeMatrix(pool.buf, idx, m, jitter, false);
            // impostor cross card: same place, yaw +90 deg, for volume
            if (slot === 'far' && inst.type.startsWith('tree')) {
              idx = take(inst.type, slot);
              if (idx < CAP) writeMatrix(pool.buf, idx, m, jitter, true);
            }
          }
        }
        if (inst.type === 'lamp' && d2 < MID_SQ) {
          const lp = this.pools.get('lightpool')?.near;
          if (lp) {
            const idx = take('lightpool', 'near');
            if (idx < CAP) writeMatrix(lp.buf, idx, m, false, false);
          }
        }
      }
    }
    // commit counts; park tails so stale matrices are invisible
    for (const [base, pools] of this.pools) {
      for (const slot of ['near', 'mid', 'far'] as const) {
        const pool = pools[slot];
        if (!pool) continue;
        const n = Math.min(written.get(`${base}:${slot}`) ?? 0, CAP);
        for (let i = n; i < pool.committed; i++) park(pool.buf, i);
        if (n === 0) park(pool.buf, 0);
        for (const mesh of pool.meshes) {
          mesh.thinInstanceCount = n === 0 ? 1 : n;
          mesh.thinInstanceBufferUpdated('matrix'); // all primitives re-upload from pool.buf
        }
        pool.committed = n === 0 ? 1 : n;
      }
    }
  }

  get stats(): { cells: number; types: number } {
    return { cells: this.entries.size, types: this.pools.size };
  }

  dispose(): void {
    for (const c of this.containers) c.dispose();
    this.root.dispose();
    this.pools.clear();
    this.entries.clear();
  }
}

/** Park a matrix at index i far below the world (invisible, cheap). */
function park(buf: Float32Array, i: number): void {
  const o = i * 16;
  buf.fill(0, o, o + 16);
  buf[o] = 1;
  buf[o + 5] = 1;
  buf[o + 10] = 1;
  buf[o + 15] = 1;
  buf[o + 13] = -10000;
}

/**
 * Write a rot_y/trans instance matrix with optional deterministic scale
 * jitter (rule 6) and yaw +90 degrees for impostor cross cards. Input
 * structure guaranteed by the exporter (see header).
 */
function writeMatrix(buf: Float32Array, i: number, m: number[], jitter: boolean, cross: boolean): void {
  const o = i * 16;
  let cy = m[0] ?? 1;
  let sy = m[8] ?? 0;
  if (cross) {
    const t = cy;
    cy = -sy;
    sy = t;
  }
  const x = m[12] ?? 0;
  const z = m[14] ?? 0;
  const q = jitter ? 0.9 + 0.25 * hash2(x, z) : 1;
  buf[o] = cy * q;
  buf[o + 1] = 0;
  buf[o + 2] = -sy * q;
  buf[o + 3] = 0;
  buf[o + 4] = 0;
  buf[o + 5] = q;
  buf[o + 6] = 0;
  buf[o + 7] = 0;
  buf[o + 8] = sy * q;
  buf[o + 9] = 0;
  buf[o + 10] = cy * q;
  buf[o + 11] = 0;
  buf[o + 12] = x;
  buf[o + 13] = m[13] ?? 0;
  buf[o + 14] = z;
  buf[o + 15] = 1;
}
